import { useCallback, useEffect, useRef } from 'react';
import { clearAllAppStorage } from '../helpers/storage/storage-migration';
import type { MetaJson } from './useVersionCheck.type';

const VERSION_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes
const BUILD_VERSION = __BUILD_VERSION__;

/**
 * Hook to check for new app versions and force an immediate reload.
 *
 * Behavior:
 * - Checks /meta.json on mount, every 15 minutes, and on tab focus
 * - If version mismatch detected, immediately clears all app localStorage and reloads
 * - Does NOT wait for navigation — forces reload as soon as mismatch is detected
 */
function useVersionCheck(): void {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isReloadingRef = useRef(false);

  const checkVersion = useCallback(async () => {
    // Skip version check in development
    if (BUILD_VERSION === 'development') {
      return;
    }

    // Prevent multiple simultaneous reload attempts
    if (isReloadingRef.current) {
      return;
    }

    try {
      // Add cache-busting query param to bypass browser cache
      const response = await fetch(`/meta.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        return;
      }

      const meta: unknown = await response.json();

      // Validate meta.json shape to prevent infinite reload from malformed responses
      if (!meta || typeof meta !== 'object' || typeof (meta as MetaJson).version !== 'string') {
        console.debug('[VersionCheck] Invalid meta.json shape, skipping');
        return;
      }

      const validMeta = meta as MetaJson;

      // Version mismatch detected — clear all app state and force reload
      if (validMeta.version !== BUILD_VERSION) {
        isReloadingRef.current = true;
        console.debug(`[VersionCheck] New version detected: ${validMeta.version} (current: ${BUILD_VERSION})`);
        clearAllAppStorage();
        window.location.reload();
      }
    } catch (error) {
      // Silently fail - network issues shouldn't break the app
      console.debug('[VersionCheck] Failed to check version:', error);
    }
  }, []);

  // Check version on mount, periodically, and on tab focus
  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Check periodically
    intervalRef.current = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

    // Check on visibility change (tab becomes active)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);
}

export { useVersionCheck };
